import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { ToastContainer, toast } from 'react-toastify';
// NOTE: Removed the direct import of CSS for `react-toastify` to fix compilation error.
// Assuming it is handled in the root component or environment.

import { DynamicForm, CategoryConfig, DeleteConfig } from '../../../../components';

const CategoryProfile = ({
  photo,
  handleImgChange,
  categoryData, // Data passed from parent (NewEmployee)
  onSubmit, // handleCategoryFormSubmit wrapped in handleSubmit from NewCategory
  isLoading,
  categories,
  uoms,
  onCancel, // onCancel prop from parent
  control, // control object from the parent 
  errors, // <-- `errors` comes from the parent
  reset, // `reset` as a prop
  isReadOnly, // for view page.
  deleting  // for delete page 
}) => {

// Dynamically add department options to CategoryConfig
let updatedCategoryConfig; // Declare outside of the if-else block
if (deleting) {
  updatedCategoryConfig = DeleteConfig;
} else {
  // Dynamically populate options for UOMs and categories
  updatedCategoryConfig = CategoryConfig?.map(field => {
    // Create a copy of the field to avoid direct mutation
    let updatedField = { ...field };

    // Check if the field is for a UOM
    if (updatedField.name === 'prices_sell_uom_id' || updatedField.name === 'prices_buy_uom_id') {
      updatedField = {
        ...updatedField,
        options: [
          { value: '', label: 'Select UOM', disabled: true, hidden: true },
          ...(Array.isArray(uoms) ? uoms : []),
        ],

      };
    }
    // Check if the field is for a department (as in the original code)
    else if (updatedField.name === 'department_id') {
      updatedField = {
        ...updatedField,
        options: [
          { value: '', label: 'Select Department', disabled: true, hidden: true },
          ...(Array.isArray(categories)? categories :[]),
        ]
  
      };
    }
    return updatedField;
  });
}

//  Use useEffect to reset the form with `categoryData` when it changes
useEffect(() => {
  if (categoryData && reset) {
    // Create a flattened data object for react-hook-form
    reset({
      ...categoryData,
      name: categoryData.name,
      barcode: categoryData.barcode,
      // Ensure we map parent_category from data to the form field name category_id
      category_id: categoryData.parent_category, 
      // Map the nested prices data to the top level for the DynamicForm
      prices_buy_uom_id: categoryData.prices?.buy_uom_id,
      prices_sell_uom_id: categoryData.prices?.sell_uom_id,
      // FIX: Map the new price fields
      buy_price: categoryData.prices?.buy_price_new,
      sell_price: categoryData.prices?.sell_price_new,
      
    });
  }
}, [categoryData, reset]);
  
  // NOTE: Removed the redundant useEffect to log errors, rely on form validation.
  // NOTE: Removed the unnecessary local `handleFormSubmit` wrapper function.

  //This will call NewEmployee's handleCancel
  const handleProfileCancel = () => {if (onCancel) {onCancel(); }};

  // CategoryProfile component: return block

return (
  // The onSubmit prop here receives handleSubmit(handleCategoryFormSubmit) from the parent.
  <form onSubmit={onSubmit} className="space-y-8"> 
     {/* CRITICAL CHANGE: Use md:grid-cols-[auto_auto] to force both columns to shrink to their content width.
       Reverted gap to gap-2 for minimal visual separation (gap-0 looks too tight).
     */}
     <div className="grid grid-cols-1 md:grid-cols-[auto_auto] gap-2 w-fit mx-auto items-start">
       
          {/* Photo Upload Section (Column 1) */}
          <div className="flex flex-col items-center gap-2 bg-white/5 p-4 rounded-xl backdrop-blur-sm w-fit">
              <div className="flex relative group">
              {/* Conditional class for image size is added here */}
                  <img
                      src={photo}
                      alt="Profile"
                      className={`${isReadOnly || deleting ? 'h-32 w-32 p-2 ring-4' : 'h-20 w-20 p-1 ring-2'} rounded-full object-cover ring-cyan-400 group-hover:ring-cyan-300 transition duration-300  mx-auto`} 
                  />
      {!(isReadOnly || deleting) && (
        <label
          htmlFor="logo-upload"
          className="absolute bottom-0 right-0 bg-cyan-600 p-1 rounded-full shadow-lg cursor-pointer hover:bg-cyan-400 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-4 w-4 text-white"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.827 6.175A2.31 2.31 0 015.18 8.077L3.465 14.887a2.122 2.122 0 00-.226 1.096v1.391a2.122 2.122 0 002.122 2.122h13.149a2.122 2.122 0 002.122-2.122v-1.391a2.122 2.122 0 00-.226-1.096L18.82 8.077a2.31 2.31 0 01-1.647-1.902V4.5a.75.75 0 00-.75-.75h-8.25a.75.75 0 00-.75.75v1.675zM12 18.5a.75.75 0 110-1.5.75.75 0 010 1.5z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 15a3 3 0 100-6 3 3 0 000 6z"
            />
          </svg>
          <input
            id="logo-upload"
            type="file"
            name="logo"
            accept="image/*"
            onChange={handleImgChange}
            className="hidden"
          />
        </label>
      )}
  </div>
  {!(isReadOnly || deleting) && (
      <div className="text-center">
        {/* Increased font size from text-sm to text-base */}
        <p className="text-base text-cyan-200">Upload profile photo</p>
        {/* Increased font size from text-xs to text-sm */}
        <p className="text-sm text-slate-300">Max size: 2MB | Format: JPG, PNG</p>
      </div>
  )}
</div>

      {/* Manual fields controlled by react-hook-form (Column 2) */}
      {/* CRITICAL CHANGE: Added w-fit to force this container to only be as wide as its content */}
      <div className="relative space-y-2 m-4 w-fit"> 
        <div>
          {/* Increased font size from text-sm to text-base */}
          <label htmlFor="name" className="text-base text-cyan-300">Category Name</label>
          {isReadOnly || deleting? (
            // Increased font size to text-lg
            <p className="text-white text-lg">{categoryData?.name || 'N/A'}</p>
          ) : (
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Category name is required' }} // <-- KEEP mandatory rule for name
         
            render={({ field }) => (
              <input
                {...field}
                id="name"
                type="text"
                // Added text-lg for input text
                className={`w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-500 text-lg`}
                placeholder="Product Name"
              />
            )}
          />
        )}
          {/* Increased font size from text-xs to text-sm */}
          {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
        )}
        </div>

<div>
{/* Increased font size from text-sm to text-base */}
<label htmlFor="product_category" className="text-base text-cyan-300">Parent Category</label>
{isReadOnly || deleting ? (
  // Increased font size to text-lg
  <p className="text-white text-lg">{categoryData?.parent_category || 'N/A'}</p>
) : (
  <Controller
    name="category_id"
    defaultValue={categoryData?.parent_category || ''}
    control={control}
    // Removed the 'required' rule to make this field optional
    render={({ field }) => (
      <select
        {...field}
        id="category_id"
        // Added text-lg for select text
        className={`w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-500 text-lg`}   
      >
        <option value="">
          Select product category
        </option>
        {categories?.map(cat => (
          <option key={cat.value} value={cat.value}>
            {cat.label}
          </option>
        ))}
      </select>
    )}
  />
)}
{/* Removed error display since the field is no longer mandatory */}
</div>


        
      </div>
    </div>

    {/* The rest of the fields rendered by DynamicForm */}
  <div className="md:col-span-2">
      <DynamicForm
        fields={updatedCategoryConfig}
        data={categoryData}
        // CRITICAL FIX: Pass the RHF handler directly to DynamicForm
        onSubmit={onSubmit} 
        isLoading={isLoading}
        control={control} // Pass shared control to DynamicForm
        onCancel={handleProfileCancel} // Pass the cancel handler to DynamicForm
        errors={errors} // <-- Pass the errors object 
        isReadOnly={isReadOnly}
      />
    </div> 
  </form>
);
}
export default CategoryProfile;
